import React, { useState, useEffect } from 'react';
import { Settings, Save, Bell, Clock, CheckCircle2, Wand2, Pause, Timer, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const Toggle = ({ checked, onChange, disabled }) => (
    <label className="flex items-center cursor-pointer">
        <div className="relative">
            <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} disabled={disabled} />
            <div className={`block w-10 h-6 rounded-full transition-colors ${disabled ? 'opacity-50' : ''} ${checked ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
            <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${checked ? 'transform translate-x-4' : ''}`}></div>
        </div>
    </label>
);

const ACTIVITY_TYPES = [
    { key: 'task', label: 'Tasks', color: 'blue' },
    { key: 'survey', label: 'Surveys', color: 'purple' },
    { key: 'compliance', label: 'Compliance', color: 'amber' },
    { key: 'course_item', label: 'Course Items', color: 'green' }
];

const ActivityTypeFilter = ({ selected = [], onChange, disabled }) => (
    <div className="flex flex-wrap gap-1.5 mt-2">
        {ACTIVITY_TYPES.map(type => {
            const isSelected = selected.includes(type.key);
            const colors = {
                blue: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', activeBg: 'bg-blue-600', activeText: 'text-white' },
                purple: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', activeBg: 'bg-purple-600', activeText: 'text-white' },
                amber: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', activeBg: 'bg-amber-600', activeText: 'text-white' },
                green: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', activeBg: 'bg-green-600', activeText: 'text-white' }
            }[type.color];
            return (
                <button
                    key={type.key}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                        const next = isSelected
                            ? selected.filter(k => k !== type.key)
                            : [...selected, type.key];
                        if (next.length > 0) onChange(next); // prevent empty
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${isSelected
                            ? `${colors.activeBg} ${colors.activeText} border-transparent`
                            : `${colors.bg} ${colors.text} ${colors.border} hover:opacity-80`
                        }`}
                >
                    {type.label}
                </button>
            );
        })}
    </div>
);

const RULES = [
    {
        key: 'overdue',
        icon: Bell,
        title: 'Overdue Activity',
        description: 'Notify when activities pass their deadline',
        iconColor: 'text-red-500',
        iconBg: 'bg-red-50',
        fields: ['recipients', 'activityTypes']
    },
    {
        key: 'approaching',
        icon: Clock,
        title: 'Approaching Deadline',
        description: 'Notify before activities become overdue',
        iconColor: 'text-amber-500',
        iconBg: 'bg-amber-50',
        fields: ['threshold', 'recipients', 'activityTypes']
    },
    {
        key: 'completion',
        icon: CheckCircle2,
        title: 'Activity Completion',
        description: 'Notify admins when activities are completed',
        iconColor: 'text-green-500',
        iconBg: 'bg-green-50',
        fields: ['notifyAdmin', 'activityTypes']
    },
    {
        key: 'stalled',
        icon: Pause,
        title: 'Stalled Activity',
        description: 'Detect activities with no progress for a set period',
        iconColor: 'text-orange-500',
        iconBg: 'bg-orange-50',
        isNew: true,
        fields: ['stalledThreshold', 'recipients', 'activityTypes']
    }
];

const NotificationSettingsPanel = ({ analyticsContext }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSuggesting, setIsSuggesting] = useState(false);
    const [isCreatingNotice, setIsCreatingNotice] = useState(false);
    const [suggestion, setSuggestion] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    const defaultTypes = ['task', 'survey', 'compliance', 'course_item'];

    const [settings, setSettings] = useState({
        overdue_enabled: true,
        overdue_recipients: 'employee_and_admin',
        overdue_activity_types: [...defaultTypes],
        approaching_enabled: true,
        approaching_threshold_hours: 24,
        approaching_recipients: 'employee_only',
        approaching_activity_types: [...defaultTypes],
        completion_enabled: true,
        completion_notify_admin: true,
        completion_activity_types: [...defaultTypes],
        stalled_enabled: false,
        stalled_threshold_days: 3,
        stalled_recipients: 'employee_and_admin',
        stalled_activity_types: [...defaultTypes],
        automated_cooldown_hours: 48
    });

    useEffect(() => {
        if (isExpanded) fetchSettings();
    }, [isExpanded]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/analytics/notification-settings');
            if (res.data.success && res.data.settings) {
                setSettings(prev => ({ ...prev, ...res.data.settings }));
            }
        } catch (err) {
            console.error('Failed to load notification settings:', err);
            toast.error('Failed to load notification settings');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveSettings = async () => {
        setIsSaving(true);
        try {
            await api.put('/analytics/notification-settings', settings);
            toast.success('Notification settings saved');
            setHasChanges(false);
        } catch (err) {
            console.error('Failed to save settings:', err);
            toast.error('Failed to save notification settings');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSuggestReminder = async () => {
        setIsSuggesting(true);
        setSuggestion(null);
        try {
            const res = await api.post('/analytics/suggest-reminder', { analyticsContext });
            if (res.data.success && res.data.suggestion) {
                setSuggestion(res.data.suggestion);
                toast.success('AI reminder drafted');
            } else {
                toast.error(res.data.message || 'Failed to generate reminder');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to suggest reminder');
        } finally {
            setIsSuggesting(false);
        }
    };

    const handleCreateNotice = async () => {
        if (!suggestion) return;
        setIsCreatingNotice(true);
        try {
            await api.post('/notices', {
                title: suggestion.title,
                content: suggestion.content,
                phase: suggestion.phase || 'active',
                priority: suggestion.priority || 'high',
                assignment_type: 'all',
                status: 'published',
                show_popup_on_login: true,
                is_reminder: true
            });
            toast.success('Notice published!');
            setSuggestion(null);
        } catch (err) {
            toast.error('Failed to publish notice');
        } finally {
            setIsCreatingNotice(false);
        }
    };

    const updateSetting = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
        setHasChanges(true);
    };

    const enabledCount = [
        settings.overdue_enabled,
        settings.approaching_enabled,
        settings.completion_enabled,
        settings.stalled_enabled
    ].filter(Boolean).length;

    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Header / Toggle */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-blue-50 rounded-lg">
                        <Bell className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="text-left">
                        <h3 className="font-semibold text-gray-800">Smart Notifications & Reminders</h3>
                        <p className="text-xs text-gray-500">
                            {enabledCount} automation rule{enabledCount !== 1 ? 's' : ''} active · Cooldown: {settings.automated_cooldown_hours}h
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {hasChanges && <span className="w-2 h-2 bg-amber-400 rounded-full" title="Unsaved changes" />}
                    <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
            </button>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="border-t border-gray-100">
                    {isLoading ? (
                        <div className="py-8 text-center text-gray-500">Loading settings...</div>
                    ) : (
                        <div className="p-5 space-y-5">
                            {/* ── Automation Rules ── */}
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <Settings size={12} />
                                    Automation Rules
                                </h4>
                                <div className="space-y-3">
                                    {RULES.map(rule => {
                                        const enabledKey = `${rule.key}_enabled`;
                                        const isEnabled = settings[enabledKey];
                                        const Icon = rule.icon;

                                        return (
                                            <div key={rule.key} className={`p-4 rounded-lg border transition-colors ${isEnabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'}`}>
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className={`p-1.5 rounded-lg ${rule.iconBg}`}>
                                                            <Icon size={14} className={rule.iconColor} />
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <p className="font-medium text-gray-800 text-sm">{rule.title}</p>
                                                                {rule.isNew && (
                                                                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold uppercase">New</span>
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>
                                                        </div>
                                                    </div>
                                                    <Toggle
                                                        checked={isEnabled}
                                                        onChange={(e) => updateSetting(enabledKey, e.target.checked)}
                                                    />
                                                </div>

                                                {/* Rule-specific fields */}
                                                {isEnabled && (
                                                    <div className="mt-3 pl-9 space-y-2.5">
                                                        {/* Recipients field */}
                                                        {rule.fields.includes('recipients') && (
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-xs text-gray-500 w-20 flex-shrink-0">Recipients:</span>
                                                                <select
                                                                    className="flex-1 text-sm rounded-md border border-gray-200 py-1.5 px-2.5 bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                                    value={settings[`${rule.key}_recipients`]}
                                                                    onChange={(e) => updateSetting(`${rule.key}_recipients`, e.target.value)}
                                                                >
                                                                    <option value="employee_only">Employee Only</option>
                                                                    <option value="admin_only">Admin Only</option>
                                                                    <option value="employee_and_admin">Employee & Admin</option>
                                                                </select>
                                                            </div>
                                                        )}

                                                        {/* Approaching threshold */}
                                                        {rule.fields.includes('threshold') && (
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-xs text-gray-500 w-20 flex-shrink-0">Threshold:</span>
                                                                <select
                                                                    className="flex-1 text-sm rounded-md border border-gray-200 py-1.5 px-2.5 bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                                    value={settings.approaching_threshold_hours}
                                                                    onChange={(e) => updateSetting('approaching_threshold_hours', parseInt(e.target.value))}
                                                                >
                                                                    <option value={12}>12 Hours Before</option>
                                                                    <option value={24}>24 Hours Before</option>
                                                                    <option value={48}>48 Hours Before</option>
                                                                    <option value={72}>3 Days Before</option>
                                                                    <option value={120}>5 Days Before</option>
                                                                    <option value={168}>7 Days Before</option>
                                                                </select>
                                                            </div>
                                                        )}

                                                        {/* Stalled threshold */}
                                                        {rule.fields.includes('stalledThreshold') && (
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-xs text-gray-500 w-20 flex-shrink-0">No progress:</span>
                                                                <select
                                                                    className="flex-1 text-sm rounded-md border border-gray-200 py-1.5 px-2.5 bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                                    value={settings.stalled_threshold_days}
                                                                    onChange={(e) => updateSetting('stalled_threshold_days', parseInt(e.target.value))}
                                                                >
                                                                    <option value={2}>2 Days</option>
                                                                    <option value={3}>3 Days</option>
                                                                    <option value={5}>5 Days</option>
                                                                    <option value={7}>7 Days</option>
                                                                    <option value={14}>14 Days</option>
                                                                </select>
                                                            </div>
                                                        )}

                                                        {/* Completion: notify admin checkbox */}
                                                        {rule.fields.includes('notifyAdmin') && (
                                                            <label className="flex items-center gap-2 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    className="rounded text-blue-600 border-gray-300"
                                                                    checked={settings.completion_notify_admin}
                                                                    onChange={(e) => updateSetting('completion_notify_admin', e.target.checked)}
                                                                />
                                                                <span className="text-sm text-gray-700">Notify Admin automatically</span>
                                                            </label>
                                                        )}

                                                        {/* Activity type filter */}
                                                        {rule.fields.includes('activityTypes') && (
                                                            <div>
                                                                <span className="text-xs text-gray-500">Applies to:</span>
                                                                <ActivityTypeFilter
                                                                    selected={settings[`${rule.key}_activity_types`] || defaultTypes}
                                                                    onChange={(types) => updateSetting(`${rule.key}_activity_types`, types)}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* ── Cooldown Setting ── */}
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                                <div className="flex items-center gap-2">
                                    <Timer size={14} className="text-gray-500" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">Automated Cooldown</p>
                                        <p className="text-xs text-gray-500">Minimum time between automated reminders per employee</p>
                                    </div>
                                </div>
                                <select
                                    className="text-sm rounded-md border border-gray-200 py-1.5 px-2.5 bg-white focus:ring-1 focus:ring-blue-500"
                                    value={settings.automated_cooldown_hours}
                                    onChange={(e) => updateSetting('automated_cooldown_hours', parseInt(e.target.value))}
                                >
                                    <option value={12}>12 Hours</option>
                                    <option value={24}>24 Hours</option>
                                    <option value={48}>48 Hours</option>
                                    <option value={72}>3 Days</option>
                                    <option value={168}>7 Days</option>
                                </select>
                            </div>

                            {/* ── Save Button ── */}
                            <button
                                onClick={handleSaveSettings}
                                disabled={isSaving || !hasChanges}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${hasChanges
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                {isSaving ? 'Saving...' : <><Save size={16} />{hasChanges ? 'Save Changes' : 'Settings Saved'}</>}
                            </button>

                            {/* ── AI Quick Reminder ── */}
                            <div className="border-t border-gray-100 pt-5">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <Wand2 size={12} />
                                    Quick AI Reminder
                                </h4>
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                                    <p className="text-sm text-gray-600 mb-3">
                                        Let AI analyze pending and overdue activities across tasks, surveys, and courses to draft a company-wide reminder notice.
                                    </p>

                                    {!suggestion ? (
                                        <button
                                            onClick={handleSuggestReminder}
                                            disabled={isSuggesting}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm shadow-sm disabled:opacity-50"
                                        >
                                            {isSuggesting ? (
                                                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Drafting Notice...</>
                                            ) : (
                                                <><Wand2 size={16} /> Draft Smart Reminder</>
                                            )}
                                        </button>
                                    ) : (
                                        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                                            <div className="flex justify-between items-start mb-2">
                                                <h5 className="font-bold text-gray-800 text-sm pr-4">{suggestion.title}</h5>
                                                <button onClick={() => setSuggestion(null)} className="text-gray-400 hover:text-gray-600 text-xs">Clear</button>
                                            </div>
                                            <div
                                                className="text-sm text-gray-600 mb-3 prose prose-sm max-w-none prose-p:leading-snug"
                                                dangerouslySetInnerHTML={{ __html: suggestion.content }}
                                            />
                                            <button
                                                onClick={handleCreateNotice}
                                                disabled={isCreatingNotice}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors text-sm shadow-sm disabled:opacity-50"
                                            >
                                                {isCreatingNotice ? 'Publishing...' : <><Bell size={16} /> Publish as Notice</>}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationSettingsPanel;
